import { useRef, useState, useCallback, useEffect } from 'react';
import { connect, Room, RemoteParticipant, LocalTrackPublication, createLocalVideoTrack, createLocalAudioTrack, LocalVideoTrack, LocalAudioTrack } from 'twilio-video';

export function useTwilioVideo() {
  const roomRef = useRef<Room | null>(null);
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [localVideoTrack, setLocalVideoTrack] = useState<LocalVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<LocalAudioTrack | null>(null);

  const connectToRoom = useCallback(async (token: string, roomName: string) => {
    try {
      const room = await connect(token, {
        name: roomName,
        // Quality knobs for clinical calls
        bandwidthProfile: { 
          video: { 
            mode: 'collaboration' as const,
            maxSubscriptionBitrate: 2500000
          } 
        },
        dominantSpeaker: true,
        networkQuality: { local: 1, remote: 1 }
      });

      roomRef.current = room;
      setIsConnected(true);

      const onConnected = (p: RemoteParticipant) => {
        setParticipants(prev => {
          // Avoid duplicates
          if (prev.some(x => x.sid === p.sid)) {
            return prev;
          }
          return [...prev, p];
        });
      };

      const onDisconnected = (p: RemoteParticipant) => {
        setParticipants(prev => prev.filter(x => x.sid !== p.sid));
      };

      // Add existing participants
      room.participants.forEach(onConnected);

      // Listen for new participants
      room.on('participantConnected', onConnected);
      room.on('participantDisconnected', onDisconnected);

      // Handle room disconnection
      room.on('disconnected', () => {
        setIsConnected(false);
        setParticipants([]);
        room.removeAllListeners();
        roomRef.current = null;
      });

      return room;
    } catch (error) {
      console.error('Failed to connect to Twilio room:', error);
      throw error;
    }
  }, []);

  const addLocalVideo = useCallback(async (deviceId?: string) => {
    try {
      const constraints: MediaTrackConstraints = {
        width: { ideal: 1280 },
        height: { ideal: 720 }
      };
      
      if (deviceId) {
        constraints.deviceId = { exact: deviceId };
      }

      const track = await createLocalVideoTrack(constraints);
      setLocalVideoTrack(track);
      
      if (roomRef.current) {
        roomRef.current.localParticipant.publishTrack(track);
      }
      
      return track;
    } catch (error) {
      console.error('Failed to create local video track:', error);
      throw error;
    }
  }, []);

  const addLocalAudio = useCallback(async (deviceId?: string) => {
    try {
      const constraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      
      if (deviceId) {
        constraints.deviceId = { exact: deviceId };
      }

      const track = await createLocalAudioTrack(constraints);
      setLocalAudioTrack(track);
      
      if (roomRef.current) {
        roomRef.current.localParticipant.publishTrack(track);
      }
      
      return track;
    } catch (error) {
      console.error('Failed to create local audio track:', error);
      throw error;
    }
  }, []);

  const toggleMute = useCallback((muted: boolean) => {
    if (localAudioTrack) {
      localAudioTrack.enable(!muted);
    }
  }, [localAudioTrack]);

  const toggleVideo = useCallback((enabled: boolean) => {
    if (localVideoTrack) {
      localVideoTrack.enable(enabled);
    }
  }, [localVideoTrack]);

  const leaveRoom = useCallback(() => {
    const room = roomRef.current;
    if (!room) {
      // Already disconnected or never connected
      return;
    }

    try {
      // Stop and unpublish local tracks
      if (localVideoTrack) {
        try {
          localVideoTrack.stop();
          room.localParticipant.unpublishTrack(localVideoTrack);
        } catch (err) {
          console.warn('Error stopping video track:', err);
        }
        setLocalVideoTrack(null);
      }
      
      if (localAudioTrack) {
        try {
          localAudioTrack.stop();
          room.localParticipant.unpublishTrack(localAudioTrack);
        } catch (err) {
          console.warn('Error stopping audio track:', err);
        }
        setLocalAudioTrack(null);
      }

      // Stop all remaining tracks
      try {
        room.localParticipant.tracks.forEach((pub: LocalTrackPublication) => {
          if (pub.track) {
            // Check if track has stop method (LocalVideoTrack or LocalAudioTrack)
            if ('stop' in pub.track && typeof pub.track.stop === 'function') {
              pub.track.stop();
            }
          }
        });
      } catch (err) {
        console.warn('Error stopping tracks:', err);
      }

      // Disconnect from room
      try {
        room.disconnect();
      } catch (err) {
        console.warn('Error disconnecting room:', err);
      }
    } finally {
      roomRef.current = null;
      setIsConnected(false);
      setParticipants([]);
    }
  }, [localVideoTrack, localAudioTrack]);

  // Cleanup on unmount only (empty deps to prevent re-running)
  useEffect(() => {
    return () => {
      // Only cleanup on actual unmount
      if (roomRef.current) {
        leaveRoom();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on unmount

  return {
    room: roomRef.current,
    isConnected,
    participants,
    localVideoTrack,
    localAudioTrack,
    connectToRoom,
    addLocalVideo,
    addLocalAudio,
    toggleMute,
    toggleVideo,
    leaveRoom
  };
}

